import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useVisits } from "@/hooks/useVisits";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingScreen } from "@/components/LoadingScreen";
import { AppointmentCalendar } from "@/components/AppointmentCalendar";
import { AddToCalendar } from "@/components/AddToCalendar";
import { BookingDialog } from "@/components/BookingDialog";
import type { VisitRow, VisitStatus } from "@/lib/database.types";

const badgeFor: Record<VisitStatus, { tone: "active" | "pending" | "muted" | "warn"; label: string }> = {
  scheduled: { tone: "active", label: "Scheduled" },
  completed: { tone: "muted",  label: "Completed" },
  cancelled: { tone: "warn",   label: "Cancelled" },
  no_show:   { tone: "warn",   label: "No-show" },
};

export default function Visits() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const visitsQ = useVisits(patientQ.data?.id);
  const [bookingOpen, setBookingOpen] = useState(false);

  if (patientQ.isLoading || visitsQ.isLoading) return <LoadingScreen />;

  const now = new Date();
  const visits = visitsQ.data ?? [];
  const upcoming = visits.filter(
    (v) => v.scheduled_at && new Date(v.scheduled_at) >= now && v.status === "scheduled",
  );
  const past = visits.filter((v) => !upcoming.includes(v));

  return (
    <AppLayout>
      <PageHeader
        title="Visits"
        subtitle="Upcoming appointments and visit history."
        right={
          <Button size="md" onClick={() => setBookingOpen(true)}>
            + Book appointment
          </Button>
        }
      />

      <div className="mb-6">
        <Card title="Calendar">
          <AppointmentCalendar
            visits={visits}
            onBook={() => setBookingOpen(true)}
          />
        </Card>
      </div>

      {visits.length === 0 ? (
        <EmptyState
          title="No visits yet"
          body="Once you book a visit it'll show up on the calendar and in this list."
          cta={
            <Button onClick={() => setBookingOpen(true)}>
              Book an appointment
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {upcoming.length > 0 && (
            <section>
              <h2 className="font-serif text-[18px] text-fh-ink mb-3">Upcoming</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {upcoming.map((v) => <VisitCard key={v.id} visit={v} />)}
              </div>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h2 className="font-serif text-[18px] text-fh-ink mb-3">Past</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {past.map((v) => <VisitCard key={v.id} visit={v} />)}
              </div>
            </section>
          )}
        </div>
      )}

      <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </AppLayout>
  );
}

function VisitCard({ visit }: { visit: VisitRow }) {
  const badge = badgeFor[visit.status];
  const when = visit.scheduled_at ? new Date(visit.scheduled_at) : null;
  const canJoin = visit.status === "scheduled" && !!visit.video_link;

  return (
    <Card
      title={visit.provider?.full_name ?? "Provider TBD"}
      action={badge ? <StatusBadge label={badge.label} tone={badge.tone} /> : undefined}
    >
      <div className="text-[14px] text-fh-ink mb-1">
        {when ? (
          <>
            {when.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            {" · "}
            {when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </>
        ) : (
          <span className="text-fh-gray">Time TBD</span>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {canJoin && (
          <a
            href={visit.video_link!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-fh-green hover:bg-fh-green-dark px-4 py-2 text-[13px] font-semibold text-white"
          >
            Join video visit →
          </a>
        )}
        <AddToCalendar visit={visit} size="md" />
      </div>
    </Card>
  );
}
